import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { buildWatchlistSecurityOptions, normalizeCogniveId } from '../../utils/posSizingWatchlistSelection';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import { securityIdentityPresentation } from '../../utils/securityIdentityPresentation';
import { CountryFlagText } from '../shared/securityDisplayPrimitives';
import DataTable from '../data_table/DataTable';
import { sortByText } from '../data_table/sorting';
import Tooltip from '../ui/Tooltip';

const ALREADY_ADDED_TOOLTIP = 'Already in the Position Sizing portfolio.';
const INITIAL_SORTING = Object.freeze([{ id: 'identity_summary', desc: false }]);
const NO_SELECTION = Object.freeze({});
// Securities already in Position Sizing show dimmed and cannot be ticked.
const ALREADY_ADDED_ROW_CLASS_NAME = 'opacity-[0.48]';

export default function AddWatchlistSecuritiesDialog({
    open,
    watchlistRows,
    existingRows,
    onClose,
    onConfirm,
}) {
    const [search, setSearch] = useState('');
    // Ticked securities ({ [id]: true }), kept while the search hides them.
    const [rowSelection, setRowSelection] = useState(NO_SELECTION);
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const options = useMemo(
        () => buildWatchlistSecurityOptions(watchlistRows),
        [watchlistRows],
    );
    const existingIds = useMemo(
        () => new Set(
            (Array.isArray(existingRows) ? existingRows : [])
                .map((row) => normalizeCogniveId(row?.cognive_id))
                .filter(Boolean),
        ),
        [existingRows],
    );
    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((row) => [
            row.identifier_type,
            row.identifier,
            row.ticker,
            row.company_name,
            row.country,
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [options, search]);

    useEffect(() => {
        if (!open) return;
        setSearch('');
        setRowSelection(NO_SELECTION);
        setIdentityDisplayField('company_name');
    }, [open]);

    const isSelectable = (row) => !existingIds.has(row.cognive_id);
    const getRowClassName = (row) => (isSelectable(row) ? undefined : ALREADY_ADDED_ROW_CLASS_NAME);
    const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    const columns = useMemo(() => [
        {
            id: 'identity_summary',
            header: securityIdentityFieldLabel(identityDisplayField),
            minSize: 240,
            accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
            sortFn: sortByText,
            cell: ({ row: { original } }) => {
                const presentation = securityIdentityPresentation(original, identityDisplayField);
                const alreadyAdded = existingIds.has(original.cognive_id);
                return (
                    <Tooltip
                        content={alreadyAdded
                            ? ALREADY_ADDED_TOOLTIP
                            : presentation.tooltip || presentation.value}
                    >
                        <span className="block truncate">{presentation.value}</span>
                    </Tooltip>
                );
            },
            meta: {
                grow: true,
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityDisplayField} onChange={setIdentityDisplayField} />
                ),
            },
        },
        {
            id: 'country',
            header: 'Country',
            size: 130,
            accessorFn: (row) => row.country,
            sortFn: sortByText,
            cell: ({ getValue }) => <CountryFlagText country={getValue()} />,
        },
    ], [existingIds, identityDisplayField]);

    const handleClose = () => {
        setSearch('');
        setRowSelection(NO_SELECTION);
        onClose?.();
    };

    const handleConfirm = () => {
        const selected = new Set(selectedIds);
        const selectedRows = options
            .filter((row) => selected.has(row.id) && !existingIds.has(row.cognive_id))
            .map((row) => row.source);
        onConfirm?.(selectedRows);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Add Watchlist Securities</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Select securities from the current watchlist. They will be added as unsaved
                    position-sizing rows.
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    label="Search watchlist securities"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search company, ticker, submitted identifier, or country"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ mb: 1.5 }}
                />
                <DataTable
                    aria-label="Watchlist securities to add"
                    data={filteredOptions}
                    columns={columns}
                    initialSorting={INITIAL_SORTING}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection={isSelectable}
                    getRowClassName={getRowClassName}
                    height={420}
                    virtualize
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={selectedIds.length === 0}
                >
                    {selectedIds.length ? `Add ${selectedIds.length}` : 'Add selected'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

AddWatchlistSecuritiesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    watchlistRows: PropTypes.array,
    existingRows: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};
